/**
 * Municipality Interaction Log — Phase 1A
 * ─────────────────────────────────────────────────────────────────────
 * Append-only ledger of every coordinator ↔ municipality touchpoint
 * (call, email, portal message, in-person, voicemail) tied to a job's
 * permit lifecycle.
 *
 * Invariants:
 * 1. APPEND-ONLY. No mutation, no deletion. Corrections are made by
 *    appending a new entry whose `supersedesId` points at the bad one;
 *    consumers should call `isSupersededInteraction` to filter the
 *    "current truth" view.
 * 2. Municipality identity flows through canonical `MunicipalityId`
 *    (see floridaMunicipalities.ts) — never key off display name.
 * 3. `loggedBy` resolves via `useCurrentRep().name` → role label →
 *    "Unknown Operator". Never "system".
 * 4. `followUpDate` is an ISO datetime string. No relative dates
 *    ("tomorrow") — those are rejected at the form layer.
 *
 * This module owns ONLY data shape, validation, and pure resolvers.
 * UI lives in `components/permitting/MunicipalityInteractionLog.tsx`
 * and `MunicipalityInteractionForm.tsx`. Mutations are funnelled
 * through `appendMunicipalityInteraction` so the append-only contract
 * cannot be bypassed.
 */
import { z } from "zod";
import type { Job } from "@/data/types";
import type { MunicipalityId } from "@/data/floridaMunicipalities";

export type MunicipalityInteractionType =
  | "call"
  | "email"
  | "portal_message"
  | "in_person"
  | "voicemail";

export type ContactParty = "municipality" | "homeowner";

export interface MunicipalityInteraction {
  id: string;
  /**
   * Canonical municipality id, OR `"unmapped"` when the job's
   * municipality string couldn't be resolved. When `"unmapped"`,
   * `rawMunicipalityLabel` preserves what the operator was looking at.
   */
  municipalityId: MunicipalityId;
  /** Original (unresolved) municipality string — only set when municipalityId === "unmapped". */
  rawMunicipalityLabel?: string;
  type: MunicipalityInteractionType;
  contactPerson: string;
  summary: string;
  nextStep?: string;
  followUpDate?: string | null;
  loggedBy: string;
  createdAt: string;
  /** Set when this entry corrects a prior entry. The prior entry stays in the log (append-only). */
  supersedesId?: string;
  /** Whether this touchpoint was with the municipality office or the homeowner. */
  contactType?: ContactParty;
}

export const MUNICIPALITY_INTERACTION_TYPES: MunicipalityInteractionType[] = [
  "call",
  "email",
  "portal_message",
  "in_person",
  "voicemail",
];

export const INTERACTION_TYPE_LABEL: Record<MunicipalityInteractionType, string> = {
  call: "Call",
  email: "Email",
  portal_message: "Portal Message",
  in_person: "In Person",
  voicemail: "Voicemail",
};

const MAX_FREEFORM = 500;
const MAX_PERSON = 120;

export const municipalityInteractionInputSchema = z.object({
  municipalityId: z.string().min(1, "Municipality required"),
  rawMunicipalityLabel: z
    .string()
    .trim()
    .max(160, "Max 160 characters")
    .optional()
    .or(z.literal("").transform(() => undefined)),
  type: z.enum(["call", "email", "portal_message", "in_person", "voicemail"]),
  contactPerson: z
    .string()
    .trim()
    .min(1, "Who you spoke with is required")
    .max(MAX_PERSON, `Max ${MAX_PERSON} characters`),
  summary: z
    .string()
    .trim()
    .min(1, "Summary is required")
    .max(MAX_FREEFORM, `Max ${MAX_FREEFORM} characters`),
  nextStep: z
    .string()
    .trim()
    .max(MAX_FREEFORM, `Max ${MAX_FREEFORM} characters`)
    .optional()
    .or(z.literal("").transform(() => undefined)),
  followUpDate: z
    .string()
    .datetime({ offset: true, message: "Follow-up date must be a full date (no relative phrases)" })
    .nullable()
    .optional(),
  loggedBy: z.string().trim().min(1).max(120),
  supersedesId: z.string().trim().min(1).optional(),
  contactType: z.enum(["municipality", "homeowner"]).optional(),
});

export type MunicipalityInteractionInput = z.infer<typeof municipalityInteractionInputSchema>;

/**
 * Pure ID generator — module-local so tests can stub Date.now without
 * pulling crypto. Format mirrors the rest of the app (`mi-<ts>-<rand>`).
 */
function makeInteractionId(): string {
  const rand = Math.random().toString(36).slice(2, 8);
  return `mi-${Date.now()}-${rand}`;
}

/**
 * Append a new interaction onto a job's permit log. APPEND-ONLY:
 * existing entries are returned unchanged. Returns the updated `Job`
 * (or the original if `permitDetail` is missing — caller should guard).
 *
 * Throws on invalid input — callers should validate via the schema first
 * for friendly form-level errors and reserve the throw as a hard guard.
 */
export function appendMunicipalityInteraction(
  job: Job,
  input: MunicipalityInteractionInput,
): Job {
  if (!job.permitDetail) return job;
  const parsed = municipalityInteractionInputSchema.parse(input);
  const entry: MunicipalityInteraction = {
    id: makeInteractionId(),
    municipalityId: parsed.municipalityId as MunicipalityId,
    rawMunicipalityLabel:
      parsed.municipalityId === "unmapped" ? parsed.rawMunicipalityLabel : undefined,
    type: parsed.type,
    contactPerson: parsed.contactPerson,
    summary: parsed.summary,
    nextStep: parsed.nextStep,
    followUpDate: parsed.followUpDate ?? null,
    loggedBy: parsed.loggedBy,
    createdAt: new Date().toISOString(),
    supersedesId: parsed.supersedesId,
    contactType: parsed.contactType ?? "municipality",
  };
  const existing = job.permitDetail.municipalityLog ?? [];
  return {
    ...job,
    permitDetail: {
      ...job.permitDetail,
      municipalityLog: [...existing, entry],
    },
  };
}

/** Read accessor — returns chronological order (oldest first) by default. */
export function getMunicipalityLog(
  job: Job | null | undefined,
  opts: { order?: "asc" | "desc" } = {},
): MunicipalityInteraction[] {
  const log = job?.permitDetail?.municipalityLog ?? [];
  if (opts.order === "desc") return [...log].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  return log;
}

/**
 * An interaction is "superseded" if any later entry in the log points
 * back at it via `supersedesId`. Use to render the "current truth"
 * timeline while still preserving full history.
 */
export function isSupersededInteraction(
  entry: MunicipalityInteraction,
  log: MunicipalityInteraction[],
): boolean {
  return log.some((e) => e.supersedesId === entry.id);
}

/** Most-recent (by createdAt) non-superseded entry, or null. */
export function getLatestInteraction(
  job: Job | null | undefined,
): MunicipalityInteraction | null {
  const log = getMunicipalityLog(job);
  if (log.length === 0) return null;
  const live = log.filter((e) => !isSupersededInteraction(e, log));
  if (live.length === 0) return null;
  return [...live].sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];
}

/** Earliest pending follow-up (today or future), or null. */
export function getNextFollowUp(
  job: Job | null | undefined,
  now: Date = new Date(),
): MunicipalityInteraction | null {
  const log = getMunicipalityLog(job);
  const live = log.filter((e) => !isSupersededInteraction(e, log) && e.followUpDate);
  if (live.length === 0) return null;
  const sorted = [...live].sort((a, b) => (a.followUpDate ?? "").localeCompare(b.followUpDate ?? ""));
  return sorted[0] ?? null;
  void now;
}
